import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { JOKERS_PER_MONTH } from '@kraftwerk/shared';
import type { Badge, BadgeKey, Streak } from '@kraftwerk/shared';
import { db } from '../db/local.js';
import { loadAchievements } from '../db/achievements.js';
import type { AchievementsView } from '../db/achievements.js';
import { formatDay } from '../format.js';
import { useAchievementScope } from '../hooks/useModules.js';
import { useTimeZone } from '../hooks/useToday.js';

/**
 * Serien und Abzeichen (E13) — im Wochenrückblick, unter den Ringen.
 *
 * Erreichte Abzeichen mit Tag, wo die Daten ihn hergeben; je Art das nächste
 * mit Fortschritt („12 von 50"). Keine Liste von dreißig grauen Kacheln — das
 * sähe aus wie ein Mahnregister.
 */
export function Achievements(): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  // Nur, was zu den Modulen gehört — bei Teens nur Verhalten (28.10.2026).
  const scope = useAchievementScope();
  const view = useLiveQuery(
    () => (timeZone ? loadAchievements(timeZone, db) : undefined),
    [timeZone],
  ) as AchievementsView | undefined;
  if (!view || !view.enabled) return null;
  const shown = view.badges.filter((badge) => scope.badges.includes(badge.key));
  if (shown.length === 0 && scope.streaks.length === 0) return null;

  const earned = shown.filter((badge) => badge.earned).reverse();
  const next = new Map<BadgeKey, Badge>();
  for (const badge of shown) {
    if (!badge.earned && !next.has(badge.key)) next.set(badge.key, badge);
  }
  const badgeName = (badge: Badge) =>
    t(`badges.${badge.key}`, {
      count: badge.level,
      level: badge.level.toLocaleString(i18n.language),
    });

  return (
    <>
      {scope.streaks.length > 0 ? (
        <section aria-labelledby="streaks-heading" className="kw-tile flex flex-col gap-2">
          <h2 id="streaks-heading" className="kw-kicker">
            {t('streaks.heading')}
          </h2>
          <ul className="flex flex-col gap-1.5 text-sm">
            {scope.streaks.includes('supplements') ? (
              <StreakRow label={t('streaks.supplements')} unit="days" streak={view.supplements} />
            ) : null}
            {scope.streaks.includes('journal') ? (
              <StreakRow label={t('streaks.journal')} unit="days" streak={view.journal} />
            ) : null}
            {scope.streaks.includes('trainingWeeks') ? (
              <StreakRow
                label={t('streaks.trainingWeeks')}
                unit="weeks"
                streak={view.trainingWeeks}
              />
            ) : null}
          </ul>
          {scope.streaks.some((key) => key !== 'trainingWeeks') ? (
            <p className="text-xs text-ink-muted">
              {t('streaks.jokerWhy', { count: JOKERS_PER_MONTH })}
            </p>
          ) : null}
        </section>
      ) : null}

      {shown.length === 0 ? null : (
        <section aria-labelledby="badges-heading" className="kw-tile flex flex-col gap-3">
          <h2 id="badges-heading" className="kw-kicker">
            {t('badges.heading', { count: earned.length })}
          </h2>
          {earned.length === 0 ? (
            <p className="text-sm text-ink-muted">
              {t(scope.badges.includes('sessions') ? 'badges.none' : 'badges.noneYet')}
            </p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {earned.map((badge) => (
                <li key={`${badge.key}-${badge.level}`} className="kw-chip border-good text-good">
                  ★ {badgeName(badge)}
                  {badge.earnedOn ? ` · ${formatDay(badge.earnedOn, 'date', i18n.language)}` : ''}
                </li>
              ))}
            </ul>
          )}
          <ul className="flex flex-col gap-2">
            {[...next.values()].map((badge) => (
              <li key={badge.key} className="flex flex-col gap-1 text-sm">
                <span className="flex justify-between gap-2">
                  <span className="min-w-0 truncate">{badgeName(badge)}</span>
                  <span className="shrink-0 text-xs text-ink-muted">
                    {t('badges.progress', {
                      done: badge.progress.toLocaleString(i18n.language),
                      goal: badge.level.toLocaleString(i18n.language),
                    })}
                  </span>
                </span>
                <span
                  className="kw-bar"
                  role="img"
                  aria-label={t('badges.progress', {
                    done: badge.progress.toLocaleString(i18n.language),
                    goal: badge.level.toLocaleString(i18n.language),
                  })}
                >
                  <i style={{ width: `${Math.round((badge.progress / badge.level) * 100)}%` }} />
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

function StreakRow({
  label,
  unit,
  streak,
}: {
  label: string;
  unit: 'days' | 'weeks';
  streak: Streak;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <li className="flex items-baseline justify-between gap-2">
      <span className="min-w-0">{label}</span>
      <span className="shrink-0 text-right">
        <span className="kw-figure text-lg">{streak.current}</span>{' '}
        <span className="text-xs text-ink-muted">
          {t(`streaks.${unit}`, { count: streak.current })}
          {streak.best > streak.current ? ` · ${t('streaks.best', { count: streak.best })}` : ''}
          {streak.jokersUsed > 0
            ? ` · ${t('streaks.jokerUsed', { count: streak.jokersUsed })}`
            : ''}
        </span>
      </span>
    </li>
  );
}
